/**
 * 文档草稿：产生新文档的唯一合法途径（DESIGN.md §4.2、CLAUDE.md 规则 5）。
 *
 * 文档类型全字段 `readonly`，所以没有任何地方能直接改它。要改就得走这里，
 * 而这里只被 Command Bus（M1-07）调用——于是"所有修改都经 Command Bus"
 * 从一条口头纪律变成了类型系统的结论。
 *
 * 实现上是**结构共享**：改一个图层只重建从根到它的那条路径上的节点，
 * 其余子树原样复用。一棵 500 图层的文档改一个属性，新建的对象数是树的
 * 深度（个位数）而不是 500。这让"每个命令产生一个新文档"在性能上站得住，
 * 也让撤销只需换一个根引用。
 *
 * 与 TileStore 的分工：**这里只管结构，像素归 TileStore。** 图层持有
 * TileHandle，草稿复制图层时并不复制像素。
 */
import type {
  Document,
  GroupLayer,
  Guide,
  Layer,
  LayerBase,
  LayerComp,
  LayerEffect,
  LayerId,
  Mat3,
  Rect,
  SavedPath,
  SelectionState,
} from './types';

/** 在树中定位一个图层的结果。 */
export interface LayerLocation {
  readonly layer: Layer;
  /** 从根到该图层的祖先链，不含它自己。 */
  readonly ancestors: readonly GroupLayer[];
  /** 在父组 children 中的下标。 */
  readonly index: number;
}

/** 深度优先遍历图层树。用生成器：调用方常在找到目标后短路。 */
export function* walkLayers(
  root: GroupLayer,
  ancestors: readonly GroupLayer[] = [],
): Generator<{ layer: Layer; ancestors: readonly GroupLayer[]; index: number }> {
  for (const [index, layer] of root.children.entries()) {
    yield { layer, ancestors, index };
    if (layer.kind === 'group') {
      yield* walkLayers(layer, [...ancestors, layer]);
    }
  }
}

/** 按 id 定位图层。找不到返回 null——"没有那个图层"是调用方要处理的正常情况。 */
export function findLayer(root: GroupLayer, id: LayerId): LayerLocation | null {
  for (const entry of walkLayers(root)) {
    if (entry.layer.id === id) {
      return { layer: entry.layer, ancestors: entry.ancestors, index: entry.index };
    }
  }
  return null;
}

/** 图层总数（不含根组自身）。 */
export function countLayers(root: GroupLayer): number {
  let n = 0;
  for (const entry of walkLayers(root)) {
    void entry;
    n++;
  }
  return n;
}

/**
 * 在树中替换一个图层，返回新的根组。
 *
 * **结构共享**：只重建从根到目标的路径，其余子树按引用复用。
 * 传 `null` 表示删除该图层。
 */
function replaceLayer(root: GroupLayer, id: LayerId, next: Layer | null): GroupLayer {
  let changed = false;

  const children: Layer[] = [];
  for (const child of root.children) {
    if (child.id === id) {
      changed = true;
      if (next !== null) children.push(next);
      continue;
    }
    if (child.kind === 'group') {
      const rebuilt = replaceLayer(child, id, next);
      // 引用不同才说明这条分支上有改动，据此避免重建未受影响的子树
      if (rebuilt !== child) changed = true;
      children.push(rebuilt);
      continue;
    }
    children.push(child);
  }

  return changed ? { ...root, children } : root;
}

/**
 * 文档草稿。
 *
 * 用法是"取一个草稿 → 若干次修改 → commit 出新文档"。草稿本身可变，
 * 但它产出的 Document 全字段只读。commit 之后草稿失效，防止一个草稿
 * 被提交两次产生两个分叉的文档。
 */
export class DocumentDraft {
  #document: Document;
  #committed = false;
  #mutations = 0;

  private constructor(base: Document) {
    this.#document = base;
  }

  static from(document: Document): DocumentDraft {
    return new DocumentDraft(document);
  }

  /** 当前状态。每次修改后都是一个新的不可变对象。 */
  get current(): Document {
    return this.#document;
  }

  /** 本草稿上发生过多少次修改。命令可据此判断自己是否是空操作。 */
  get mutationCount(): number {
    return this.#mutations;
  }

  #assertOpen(): void {
    if (this.#committed) {
      throw new Error('草稿已提交，不能继续修改——一个草稿只能产出一个文档。');
    }
  }

  #apply(next: Document): this {
    this.#assertOpen();
    if (next !== this.#document) this.#mutations++;
    this.#document = next;
    return this;
  }

  /* ---------------- 图层操作 ---------------- */

  /** 替换一个图层。找不到该 id 时不做任何事（不抛错——命令层负责校验）。 */
  replaceLayer(id: LayerId, next: Layer): this {
    const root = replaceLayer(this.#document.root, id, next);
    return this.#apply(root === this.#document.root ? this.#document : { ...this.#document, root });
  }

  /** 用一个函数更新图层。函数返回 null 表示不改。 */
  updateLayer(id: LayerId, update: (layer: Layer) => Layer | null): this {
    const found = findLayer(this.#document.root, id);
    if (found === null) return this;
    const next = update(found.layer);
    if (next === null || next === found.layer) return this;
    return this.replaceLayer(id, next);
  }

  /** 删除图层。 */
  removeLayer(id: LayerId): this {
    const root = replaceLayer(this.#document.root, id, null);
    return this.#apply(root === this.#document.root ? this.#document : { ...this.#document, root });
  }

  /**
   * 插入图层。
   *
   * @param parentId 目标组。传 null 插入根组。
   * @param index 插入位置。超出范围时钳到两端——插入位置越界不该让操作失败。
   */
  insertLayer(parentId: LayerId | null, layer: Layer, index: number): this {
    const insertInto = (group: GroupLayer): GroupLayer => {
      const at = Math.max(0, Math.min(group.children.length, index));
      const children = [...group.children];
      children.splice(at, 0, layer);
      return { ...group, children };
    };

    if (parentId === null) {
      return this.#apply({ ...this.#document, root: insertInto(this.#document.root) });
    }

    const found = findLayer(this.#document.root, parentId);
    if (found === null || found.layer.kind !== 'group') return this;
    return this.replaceLayer(parentId, insertInto(found.layer));
  }

  /**
   * 移动图层到新的位置。
   *
   * 先删后插。若目标组是被移动图层的后代，操作被忽略——
   * 那会把一棵子树接到自己身上，产生环。
   */
  moveLayer(id: LayerId, newParentId: LayerId | null, index: number): this {
    const found = findLayer(this.#document.root, id);
    if (found === null) return this;

    if (newParentId !== null && this.#isDescendant(found.layer, newParentId)) {
      // 把组移进自己的子孙里会造出环，之后任何遍历都会死循环
      return this;
    }

    return this.removeLayer(id).insertLayer(newParentId, found.layer, index);
  }

  #isDescendant(layer: Layer, candidateId: LayerId): boolean {
    if (layer.kind !== 'group') return false;
    for (const entry of walkLayers(layer)) {
      if (entry.layer.id === candidateId) return true;
    }
    return false;
  }

  /* ---------------- 常见属性修改 ---------------- */

  setLayerName(id: LayerId, name: string): this {
    return this.updateLayer(id, (layer) => ({ ...layer, name }));
  }

  setVisible(id: LayerId, visible: boolean): this {
    return this.updateLayer(id, (layer) => ({ ...layer, visible }));
  }

  /** 展开/收起一个组（图层面板的视觉状态，只有 `kind: 'group'` 有这个字段）。 */
  setCollapsed(id: LayerId, collapsed: boolean): this {
    return this.updateLayer(id, (layer) => (layer.kind === 'group' ? { ...layer, collapsed } : null));
  }

  /** 设置不透明度。取值被钳到 0–1——越界值是调用方的 bug，但不该让文档进入非法状态。 */
  setOpacity(id: LayerId, opacity: number): this {
    const clamped = Math.max(0, Math.min(1, opacity));
    return this.updateLayer(id, (layer) => ({ ...layer, opacity: clamped }));
  }

  setFillOpacity(id: LayerId, fillOpacity: number): this {
    const clamped = Math.max(0, Math.min(1, fillOpacity));
    return this.updateLayer(id, (layer) => ({ ...layer, fillOpacity: clamped }));
  }

  setTransform(id: LayerId, transform: Mat3): this {
    return this.updateLayer(id, (layer) => ({ ...layer, transform }));
  }

  /** 图层样式（M3-14）：投影/描边等，任意图层种类都能挂。 */
  setEffects(id: LayerId, effects: readonly LayerEffect[]): this {
    return this.updateLayer(id, (layer) => ({ ...layer, effects }));
  }

  /* ---------------- 文档级 ---------------- */

  setSelection(selection: SelectionState): this {
    return this.#apply({ ...this.#document, selection });
  }

  setCanvasSize(width: number, height: number): this {
    if (width < 1 || height < 1 || !Number.isInteger(width) || !Number.isInteger(height)) {
      throw new RangeError(`画布尺寸必须是正整数，收到 ${width}×${height}。`);
    }
    return this.#apply({ ...this.#document, size: { width, height } });
  }

  /** 替换整份图层复合列表（M4-05）：增删复合、或应用复合改各图层状态后回写列表本身。 */
  setLayerComps(layerComps: readonly LayerComp[]): this {
    return this.#apply({ ...this.#document, layerComps });
  }

  /** 替换整份参考线列表（新建/删除/清空/切换锁定都落这一个通用口子）。 */
  setGuides(guides: readonly Guide[]): this {
    return this.#apply({ ...this.#document, guides });
  }

  setPaths(paths: readonly SavedPath[]): this {
    return this.#apply({ ...this.#document, paths });
  }

  /**
   * 提交，产出新文档。
   *
   * 会更新 `meta.modifiedAt`。草稿随即失效——同一个草稿提交两次会产生
   * 两个分叉的文档，而调用方几乎总是以为它们是同一个。
   */
  commit(now: string): Document {
    this.#assertOpen();
    this.#committed = true;
    if (this.#mutations === 0) return this.#document;
    return {
      ...this.#document,
      meta: { ...this.#document.meta, modifiedAt: now },
    };
  }
}

/* ------------------------------------------------------------------ */
/* 构造                                                                 */
/* ------------------------------------------------------------------ */

/**
 * 图层的公共默认值。
 *
 * 返回 {@link LayerBase} 而不是 `Omit<Layer, 'kind'>`：后者对联合类型做 Omit
 * 会分配到每个成员上，产出一个谁都对不上的交叉类型，逼得每个调用点都写
 * `as SomeLayer`——而类型断言正是这里最不该出现的东西。
 * `LayerBase` 本就不含 `kind`，展开后补上 kind 与该种特有字段即可，全程无需断言。
 */
export function layerDefaults(id: LayerId, name: string): LayerBase {
  return {
    id,
    name,
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: 'normal',
    clipped: false,
    locks: { all: false, transparency: false, position: false },
    transform: { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 },
    effects: [],
  };
}

export interface CreateDocumentOptions {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly title?: string;
  readonly dpi?: number;
  readonly createdAt: string;
}

/** 新建空文档。根组自身不算图层，也不参与合成。 */
export function createDocument(options: CreateDocumentOptions): Document {
  const rootId = `${options.id}:root` as LayerId;
  const emptyRect: Rect = { x: 0, y: 0, width: 0, height: 0 };

  const root: GroupLayer = {
    ...layerDefaults(rootId, 'root'),
    kind: 'group',
    children: [],
    passThrough: true,
    collapsed: false,
  };

  return {
    id: options.id,
    size: { width: options.width, height: options.height },
    colorMode: 'rgb',
    bitDepth: 8,
    workingSpace: { name: 'sRGB (linear)', linear: true },
    dpi: options.dpi ?? 72,
    root,
    selection: { bounds: emptyRect },
    guides: [],
    paths: [],
    layerComps: [],
    meta: {
      title: options.title ?? '未命名',
      createdAt: options.createdAt,
      modifiedAt: options.createdAt,
    },
  };
}
